<template>
  
  <div class="app" ref="refocus" tabindex="-1">
    <Header />
    <main>
      <div class="pt-28 md:pt-24 lg:pt-32 max-w-[800px] mx-auto text-center">
        <div class="mb-2 md:mb-4">
          <span class="nba rounded-full mr-2 sm:mr-0 mb-2 sm:mb-0 bg-red px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
            Error
          </span>
        </div>
        <h1 class="mt-2 text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 text-center">
          {{ message }}
        </h1>
      </div>
      <div class="mx-auto my-16 flow-root max-w-lg sm:my-20 px-4">
        <h2 class="sr-only">Popular pages</h2>
        <ul role="list" class="-mt-6">
          <li class="relative flex gap-x-6 py-6 border-b border-black">
            <div class="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-lg ring-2 ring-black">
              <Icon name="lucide:message-square-more" color="black" class="h-5 w-5 text-black" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink to="/support">
                  <span class="absolute inset-0" />
                  Support and troubleshooting
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">View troubleshooting FAQ and contact support</p>
            </div>
          </li>
          <li class="relative flex gap-x-6 py-6 border-b border-black">
            <div class="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-lg ring-2 ring-black">
              <Icon name="lucide:drum" color="black" class="h-5 w-5 text-black" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink to="/explore">
                  <span class="absolute inset-0" />
                  Explore all rudiments
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">Find the rudiment you're looking for</p>
            </div>
          </li>
          <li v-if="user" class="relative flex gap-x-6 py-6 border-b border-black">
            <div class="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-lg ring-2 ring-black">
              <Icon name="lucide:layout-dashboard" color="black" class="h-5 w-5 text-black" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink to="/play">
                  <span class="absolute inset-0" />
                  Go to dashboard
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">View your account dashboard</p>
            </div>
          </li>
          <li class="relative flex gap-x-6 py-6 border-b border-black">
            <div class="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-lg ring-2 ring-black">
              <Icon name="lucide:layout-grid" color="black" class="h-5 w-5 text-black" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink to="/collections">
                  <span class="absolute inset-0" />
                  View collections
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">See all available courses</p>
            </div>
          </li>
          <li v-if="!user" class="relative flex gap-x-6 py-6 border-b border-black">
            <div class="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-lg ring-2 ring-black">
              <Icon name="lucide:user-plus" color="black" class="h-5 w-5 text-black" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink to="/join">
                  <span class="absolute inset-0" />
                  Join now
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">Register and start free trial</p>
            </div>
          </li>
        </ul>
        <div class="mt-10 flex justify-center">
          <NuxtLink to="/" class="text-sm font-semibold leading-6 text-red">
            <span>&larr;</span>
            Back to home
          </NuxtLink>
        </div>
      </div>
    </main>
  </div>

</template>

<script setup>
const error = useError()
const user = useSupabaseUser()

const statusCode = computed(() => error.value.statusCode || 404)
const theUrl = computed(() => error.value.url || '/')

const message = computed(() => {
  if (statusCode.value === 404) {
    return 'Page not found'
  } else if (statusCode.value === 500) {
    return 'Internal server error'
  } else {
    return 'An unexpected error occurred'
  }
})

const seo = computed(() => {
  return {
    metaTitle: statusCode.value + ' | ' + theUrl,
    metaDescription: message.value,
    metaImage: null
  }
})

useHead(seo)
</script>